import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  OrganizationRoleDto,
  OrganizationRoleRequestDto,
} from 'src/api/FiretrolApiClient';
import { organizationRoleApiClient } from 'src/api/FiretrolApiClientInstances';
import { queryKeys } from 'src/api/queries/queryKeys';
import { invalidateFor } from 'src/api/queries/invalidationMap';
import { optimisticList, rollbackList } from 'src/api/queries/optimistic';

const matchById = (a: OrganizationRoleDto, b: OrganizationRoleDto) =>
  a.id === b.id;

export function useCreateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: OrganizationRoleRequestDto) =>
      organizationRoleApiClient.create(request),
    meta: { successMessage: 'Role created', suppressErrorToast: true },
    onSettled: () => invalidateFor('role.create', qc),
  });
}

export function useUpdateRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      request,
    }: {
      id: number;
      request: OrganizationRoleRequestDto;
    }) => organizationRoleApiClient.update(id, request),
    meta: { successMessage: 'Role updated', suppressErrorToast: true },
    onSettled: () => invalidateFor('role.update', qc),
  });
}

export function useDeleteRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (role: OrganizationRoleDto) =>
      organizationRoleApiClient.delete(role.id).then(() => role),
    meta: { successMessage: 'Role deleted' },
    onMutate: (role: OrganizationRoleDto) =>
      optimisticList(qc, {
        queryKey: queryKeys.organization.roles(),
        op: 'remove',
        entity: role,
        matchBy: matchById,
      }),
    onError: (_err, _role, ctx) => rollbackList(qc, ctx),
    onSettled: () => invalidateFor('role.delete', qc),
  });
}
